import { useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBadge,
  MiExpandableBox,
  MiIcon,
  MiLoader,
} from "@miplatform/components";
import {
  ATTEMPT_STATUSES,
  DATE_TIME_FORMATS,
  ONE_HOUR,
  WORK_ORDER_HOVER_STATUS_MAP,
  WORK_ORDER_STATUSES_LABELS,
  WORK_ORDER_SUBSTATUSES_LABELS,
} from "@miplatform/constants";
import {
  IAttemptListItemResponseForWorkOrder,
  LoaderType,
  StepStatus,
  WorkOrderStatus,
} from "@miplatform/types";
import {
  convertTimeToFullShortHand,
  formatDate,
  getDurationInHours,
  getFormattedDuration,
  getWorkOrderStatus,
  hoursToMinutes,
  toFormattedDate,
} from "@miplatform/utils";
import { mdiCalendarAccountOutline, mdiFileDocumentOutline } from "@mdi/js";
import { useRequiredWorkOrders } from "@mibuildweb/hooks";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { MiAttemptNumberBadge } from "../MiAttemptNumberBadge/MiAttemptNumberBadge";
import { MiAbbrWorkOrderCard } from "./MiAbbrWorkOrderCard";

export const MiWorkOrderTooltip = ({
  attempt,
}: {
  attempt: IAttemptListItemResponseForWorkOrder;
}) => {
  const theme = useTheme();
  const {
    home,
    assignment,
    endDate,
    scheduledStartDate,
    duration,
    exactExtendedTime = 0,
    expectedDuration,
    assigneeExpectedDuration,
    assignedUserName,
    orderEffectiveScheduleDate,
    attemptNumber,
  } = attempt ?? {};
  const { completedSteps, totalSteps } = useMemo(() => {
    if (!attempt?.steps)
      return {
        completedSteps: attempt.completedSteps,
        totalSteps: attempt.totalSteps,
      };

    return {
      completedSteps: attempt.steps.filter(
        (step) => step.status === StepStatus.Completed
      ).length,
      totalSteps: attempt.steps.length,
    };
  }, [attempt]);

  const { isLoading, requiredWorkOrders, getRequiredWorkOrders } =
    useRequiredWorkOrders();
  const completedWorkOrdersCount = useMemo(
    () =>
      requiredWorkOrders.filter(
        ({ workOrderStatus }) => workOrderStatus === WorkOrderStatus.Complete
      ).length,
    [requiredWorkOrders]
  );

  const { workOrder } = assignment ?? {};
  const {
    workOrderTemplateColor = theme.palette.primary.main,
    workOrderTemplateName,
  } = workOrder ?? {};
  const { address, builderName, communityName } = home ?? {};
  const { addressLine1, city, regionCode } = address ?? {};
  const { hardDueDate } = workOrder ?? {};
  const status = getWorkOrderStatus(attempt);
  const isCompletedAttempt = attempt?.attemptStatus === ATTEMPT_STATUSES.CLOSED;

  const { classes } = useStyles({
    workOrderTemplateColor,
    status,
    unableToBeAutoAssign: workOrder?.unableToBeAutoAssign,
  });

  const diffInDuration = attempt.endDate
    ? getDurationInHours(attempt.startDate, attempt.endDate) -
      getDurationInHours(attempt.scheduledStartDate, attempt.scheduledEndDate)
    : 0;
  const addressTitle = `${addressLine1}${
    builderName ? ` | ${builderName}` : ""
  }`;
  const addressInfo = `${city}, ${regionCode}${
    communityName ? `  |  ${communityName}` : ""
  }`;

  const getSignedDurationLabel = (value: number) =>
    `(${value > 0 ? "+" : "-"}${getFormattedDuration(Math.abs(value))})`;
  const inProgressDurationDiff =
    status === WorkOrderStatus.InProgress
      ? getSignedDurationLabel(exactExtendedTime)
      : null;
  const completedDurationDiff = diffInDuration
    ? getSignedDurationLabel(diffInDuration)
    : null;

  useEffect(() => {
    if (workOrder?.workOrderId) {
      getRequiredWorkOrders(workOrder.workOrderId as string);
    }
  }, [workOrder?.workOrderId]);

  return (
    <Box className={classes.popperContainer} data-testid="work-order-tooltip">
      <Box className={classes.popperHeader} data-testid="popper-header">
        <Typography noWrap className={classes.attemptTypeText}>
          {workOrderTemplateName}
        </Typography>
        <Box className={classes.attemptStatusContainer}>
          <MiBadge
            className={classes.attemptStatus}
            text={`${WORK_ORDER_STATUSES_LABELS[status]}${
              !isCompletedAttempt && workOrder?.subStatus
                ? ` / ${WORK_ORDER_SUBSTATUSES_LABELS[workOrder.subStatus]}`
                : ""
            }`}
            data-testid="attempt-status"
            fontSize={theme.typography.pxToRem(11.5)}
          />
        </Box>
      </Box>
      <Box position="relative" display="flex" flexDirection="column" gap={1}>
        {isLoading && (
          <MiLoader
            isLoading
            type={LoaderType.Linear}
            style={{ position: "absolute", top: 0 }}
          />
        )}
      </Box>
      <Box className={classes.addressContainer}>
        <Box className={classes.popperAddress}>
          <Tooltip title={addressLine1}>
            <Box height={11}>
              <Typography className={classes.addressInfo1} noWrap>
                {addressTitle}
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title={addressInfo}>
            <Box className={classes.secondaryPopperAddress}>
              <Typography className={classes.addressInfo2} noWrap>
                {addressInfo}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
        <MiAttemptNumberBadge attemptNumber={attemptNumber} />
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Scheduled</Typography>
          <Typography className={classes.addressInfo1}>
            {toFormattedDate(
              scheduledStartDate,
              DATE_TIME_FORMATS.FORMAT_MM_DD_YY
            )}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.popperTimeSection}>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Due Date</Typography>
          <Typography className={classes.timeText}>
            {hardDueDate
              ? convertTimeToFullShortHand(hardDueDate)
              : "--/--/----"}
          </Typography>
        </Box>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Complete Date</Typography>
          <Typography className={classes.timeText}>
            {endDate ? convertTimeToFullShortHand(endDate) : "--/--/----"}
          </Typography>
        </Box>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Duration</Typography>
          <Box className={classes.flexRow}>
            <Typography className={classes.timeText} data-testid="actual-timer">
              {`${Math.ceil(
                hoursToMinutes(
                  duration
                    ? (duration || 0) + exactExtendedTime
                    : (assigneeExpectedDuration ?? expectedDuration) / ONE_HOUR
                ) || 0
              )}min`}
            </Typography>
            <Typography
              className={classes.diffTotalTimeText}
              data-testid="diff-total-time"
            >
              {inProgressDurationDiff}
              {completedDurationDiff}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Steps</Typography>
          <Box className={classes.flexRow}>
            <Typography className={classes.timeText} data-testid="actual-timer">
              {`${completedSteps}/${totalSteps}`}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className={classes.orderSection}>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>
            Order Scheduled Date
          </Typography>
          <Typography className={classes.timeText}>
            {orderEffectiveScheduleDate
              ? formatDate(
                  orderEffectiveScheduleDate,
                  DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                )
              : "--/--/----"}
          </Typography>
        </Box>
        <Box className={classes.timeContainer}>
          <Typography className={classes.labelText}>Assignee</Typography>
          <Typography className={classes.timeText}>
            {assignedUserName || "--"}
          </Typography>
        </Box>
      </Box>

      {workOrder?.unableToBeAutoAssign && (
        <Box className={classes.unableToAutoAssign}>
          <MiIcon
            path={mdiCalendarAccountOutline}
            color={theme.palette.warning.main}
            size={1.4}
          />
          <Typography fontSize={theme.fontSize.small}>
            <Typography
              component="span"
              fontSize={theme.fontSize.small}
              fontWeight={theme.fontWeight.bold}
            >
              Unable to Auto-Assign:{" "}
            </Typography>
            No capacity available. Manual assignment required.
          </Typography>
        </Box>
      )}
      {requiredWorkOrders.length !== 0 && (
        <MiExpandableBox
          defaultExpanded
          title="Required Work Orders"
          sx={{ pt: 2 }}
          rightHeaderContent={
            <Box display="flex" alignItems="center" gap={1}>
              <MiIcon
                path={mdiFileDocumentOutline}
                size={0.6}
                color={theme.palette.warning.main}
                style={{
                  marginBottom: theme.spacing(0.5),
                }}
              />
              <Typography
                fontSize={theme.fontSize.small}
                fontWeight={theme.fontWeight.semiBold}
              >
                {completedWorkOrdersCount}/{requiredWorkOrders.length}
              </Typography>
            </Box>
          }
        >
          <Box display="flex" flexDirection="column" gap={1}>
            {requiredWorkOrders.map((workOrder) => (
              <MiAbbrWorkOrderCard
                key={workOrder.workOrderId}
                workOrder={workOrder}
              />
            ))}
          </Box>
        </MiExpandableBox>
      )}
    </Box>
  );
};

const useStyles = makeStyles<{
  workOrderTemplateColor: string;
  status: WorkOrderStatus;
  unableToBeAutoAssign?: boolean;
}>()((theme, { workOrderTemplateColor, status, unableToBeAutoAssign }) => {
  const { background, text, main } =
    (WORK_ORDER_HOVER_STATUS_MAP[status] as {
      background: string;
      text?: string;
      main: string;
    }) || {};

  return {
    popperContainer: {
      width: 350,
      height: "100%",
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      overflow: "hidden",
      border: `1px solid ${workOrderTemplateColor}`,
    },
    popperHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxSizing: "content-box",
      borderRadius: "3px 3px 0px 0px",
      padding: theme.spacing(1.5, 2),
      backgroundColor: workOrderTemplateColor,
      color: theme.palette.common.white,
      height: 16,
    },
    issueNavigation: {
      display: "flex",
      justifyContent: "space-between",
    },
    attemptTypeText: {
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.medium,
      ...(unableToBeAutoAssign && {
        maxWidth: 100,
      }),
    },
    addressContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      paddingRight: theme.spacing(2),
      backgroundColor: theme.palette.common.white,
    },
    popperAddress: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      height: 43,
      padding: theme.spacing(2),
      cursor: "pointer",
    },
    secondaryPopperAddress: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyItems: "center",
      maxWidth: 250,
    },
    addressInfo1: {
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.bold,
      color: theme.palette.bluegrey.dark,
      maxWidth: 190,
    },
    addressInfo2: {
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.xsmall,
      color: theme.palette.bluegrey.dark,
    },
    popperTimeSection: {
      borderRadius: "0px 0px 1px 0px",
      padding: theme.spacing(2),
      marginTop: theme.spacing(1),
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: theme.palette.grey.light,
    },
    orderSection: {
      padding: theme.spacing(2),
      display: "flex",
      gap: theme.spacing(2),
      backgroundColor: theme.palette.grey.light,
    },
    timeContainer: {
      height: 36,
    },
    flexRow: {
      display: "flex",
      gap: 4,
    },
    labelText: {
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.regular,
      color: theme.palette.grey.medium,
    },
    statusText: {
      fontWeight: theme.fontWeight.bold,
      fontSize: theme.fontSize.small,
    },
    scheduledStartDateText: {
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.small,
      color: theme.palette.red.main,
      textDecoration: "line-through",
    },
    timeText: {
      fontSize: theme.fontSize.small,
    },
    requestedDateText: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.bold,
      color: theme.palette.grey.medium,
    },
    diffTotalTimeText: {
      fontSize: theme.fontSize.small,
    },
    checkListContainer: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      padding: 8,
    },
    checkList: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      padding: 8,
      borderRadius: 4,
      backgroundColor: theme.palette.grey.lightAccent,
    },
    checkListHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    issueStatus: {
      width: 78,
      height: 25,
      borderRadius: 4,
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.medium,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: theme.palette.common.white,
    },
    issueTitle: {
      fontWeight: theme.fontWeight.bold,
      fontSize: theme.fontSize.medium,
    },
    issueDescription: {
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.small,
    },
    attemptNo: {
      color: theme.palette.common.white,
      whiteSpace: "nowrap",
      backgroundColor: theme.palette.orange.main,
      display: "flex",
      alignItems: "center",
      border: `1px solid ${theme.palette.orange.dark}`,
      height: theme.spacing(4),
      borderRadius: theme.spacing(0.5),
      ":hover": {
        backgroundColor: theme.palette.orange.medium,
        textDecoration: "underline",
        cursor: "pointer",
      },
    },
    attemptStatusContainer: {
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.spacing(1),
    },
    attemptStatus: {
      color: `${text || main} !important`,
      whiteSpace: "nowrap",
      display: "flex",
      alignItems: "center",
      height: theme.spacing(4),
      borderRadius: theme.spacing(0.5),
      backgroundColor: background,
      border: `1px solid ${main}`,
      maxWidth: 200,
    },
    unableToAutoAssign: {
      border: `1px solid ${theme.palette.warning.main}`,
      backgroundColor: theme.palette.warning.light,
      padding: theme.spacing(2),
      borderRadius: theme.spacing(1),
      gap: theme.spacing(2),
      display: "flex",
      alignItems: "center",
      margin: theme.spacing(1, 2),
    },
  };
});
